/**
 * Phase 22 Plan 22-03 — ClientEditPage
 *
 * Route: /clients/:id/edit
 * Permission gate: clients.edit
 *
 * Prefills from GET /api/clients/:id → PUT on submit.
 */

import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useEffect, type JSX, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { ClientFormFields } from '@/components/clients/ClientFormFields';
import { clientFormSchema, type ClientFormValues } from '@/components/clients/clientFormSchema';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useClientQuery, useUpdateClientMutation } from '@/hooks/useClientQueries';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';

export function ClientEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const clientQuery = useClientQuery(id);
  const updateMutation = useUpdateClientMutation();

  const form = useZodForm({
    schema: clientFormSchema,
    defaultValues: {
      name: '',
      code: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      gstNumber: '',
      panNumber: '',
      notes: '',
    },
  });

  useEffect(() => {
    const c = clientQuery.data;
    if (c === undefined) return;
    form.reset({
      name: c.name,
      code: c.code,
      contactName: c.contactName ?? '',
      contactEmail: c.contactEmail ?? '',
      contactPhone: c.contactPhone ?? '',
      address: c.address ?? '',
      gstNumber: c.gstNumber ?? '',
      panNumber: c.panNumber ?? '',
      notes: c.notes ?? '',
    });
  }, [clientQuery.data, form]);

  if (!permissions.includes('clients.edit')) {
    return <Navigate to="/clients" replace />;
  }

  if (clientQuery.isLoading) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="client-edit-loading">
        Loading client…
      </p>
    );
  }

  if (clientQuery.isError) {
    return (
      <p className="text-destructive text-sm" data-testid="client-edit-error">
        Failed to load client.
      </p>
    );
  }

  const onSubmit = form.handleSubmit((values: ClientFormValues) => {
    setErrorMessage(null);
    updateMutation.mutate(
      {
        id,
        body: {
          name: values.name,
          code: values.code,
          ...(values.contactName && values.contactName !== '' ? { contactName: values.contactName } : {}),
          ...(values.contactEmail && values.contactEmail !== '' ? { contactEmail: values.contactEmail } : {}),
          ...(values.contactPhone && values.contactPhone !== '' ? { contactPhone: values.contactPhone } : {}),
          ...(values.address && values.address !== '' ? { address: values.address } : {}),
          ...(values.gstNumber && values.gstNumber !== '' ? { gstNumber: values.gstNumber } : {}),
          ...(values.panNumber && values.panNumber !== '' ? { panNumber: values.panNumber } : {}),
          ...(values.notes && values.notes !== '' ? { notes: values.notes } : {}),
        },
      },
      {
        onSuccess: (client) => {
          toast.success(`Client "${client.name}" updated`);
          void navigate(`/clients/${String(client.id)}`);
        },
        onError: (err) => {
          const msg = err instanceof ApiError ? (err.detail ?? err.message) : 'Unexpected error';
          setErrorMessage(msg);
          toast.error('Could not update client', { description: msg });
        },
      },
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to={`/clients/${String(id)}`}>
            <ArrowLeftIcon size={16} /> Client
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Edit Client</h1>
        <p className="text-muted-foreground">{clientQuery.data?.name}</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Client Details</CardTitle>
          <CardDescription>Update client information.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="client-edit-form">
            <ClientFormFields form={form} />

            {errorMessage !== null && (
              <div
                className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="client-edit-error-msg"
              >
                {errorMessage}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                asChild
                disabled={updateMutation.isPending}
              >
                <Link to={`/clients/${String(id)}`}>Cancel</Link>
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                data-testid="client-edit-submit"
              >
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
